import React, { useEffect, useRef } from 'react';

// Content is only allowed to start hidden once JS is running to un-hide it.
// Without this, a JS failure leaves a page of invisible text.
if (typeof document !== 'undefined') {
  document.documentElement.classList.add('js-reveal');
}

export const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Adds `is-visible` the first time the element scrolls into view, then stops
// watching it so nothing re-animates on the way back up. Content must never
// depend on the animation having run — when the visitor asks for reduced motion,
// or the browser has no IntersectionObserver, the element is shown immediately.
export const useReveal = () => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    // A hidden document (background tab, prerender, screenshot service) has its
    // rendering lifecycle throttled, so IntersectionObserver never computes and
    // the content would stay invisible forever. Show it outright instead.
    if (
      prefersReducedMotion() ||
      !('IntersectionObserver' in window) ||
      document.visibilityState !== 'visible'
    ) {
      el.classList.add('is-visible');
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          obs.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.04 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return ref;
};

// Convenience wrapper: <Reveal delay={1}>…</Reveal> stagger steps are 80ms each.
export const Reveal = ({ as: Tag = 'div', className = '', delay = 0, children, ...rest }) => {
  const ref = useReveal();
  const classes = ['reveal', delay ? `reveal--d${delay}` : '', className]
    .filter(Boolean)
    .join(' ');

  return (
    <Tag ref={ref} className={classes} {...rest}>
      {children}
    </Tag>
  );
};

export default useReveal;
